import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useSiteData } from '../context/SiteDataContext';
import { Phone, Check } from 'lucide-react';

const FilmsSolairesPage = () => {
  const { tintingPrices, tintingBenefits, companyInfo, galleryImages, pageContent } = useSiteData();
  const hero = pageContent?.filmsHero || {};

  return (
    <div className="min-h-screen bg-black font-sans">
      <Helmet>
        <title>Films Solaires & Vitres Teintées | Ecotint & Reprog - Eugénie les Bains (40)</title>
        <meta name="description" content="Pose professionnelle de films solaires à partir de 150€. Protection UV 99%, réduction chaleur, voiture de prêt gratuite. Expert à Eugénie les Bains (40)." />
        <link rel="canonical" href="https://ecotint-reprog.fr/films-solaires" />
      </Helmet>
      <Header />
      
      {/* Hero - Full screen */}
      <section className="h-screen w-full relative flex items-center" data-testid="films-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${hero.bgImage || 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=1920'})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
        
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-2xl">
            <span className="text-red-500 text-xs sm:text-sm font-medium uppercase tracking-[0.2em]">
              {hero.category || 'Protection & Style'}
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mt-3 sm:mt-4 mb-4 sm:mb-6 leading-tight">
              {hero.title || 'Films'} <span className="text-red-500">{hero.titleAccent || 'Solaires'}</span>
            </h1>
            <p className="text-sm sm:text-base md:text-xl text-white/70 max-w-xl">
              {hero.desc || "Protégez votre habitacle, améliorez votre confort et donnez un look unique à votre véhicule."}
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 sm:py-20 bg-neutral-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-red-500 text-sm font-medium uppercase tracking-[0.2em]">
              Avantages
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
              Pourquoi choisir nos <span className="text-red-500">films</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tintingBenefits.map((benefit) => (
              <div key={benefit.id} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-red-500/30 transition-colors">
                <div className="w-10 h-10 rounded-full bg-red-600/20 flex items-center justify-center mb-4">
                  <Check size={20} className="text-red-500" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{benefit.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free car banner */}
      <section className="py-6 bg-red-600">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-white font-medium text-lg">Voiture de prêt gratuite pendant la pose</p>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 sm:py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-red-500 text-sm font-medium uppercase tracking-[0.2em]">
              Tarifs {new Date().getFullYear()}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
              Grille <span className="text-red-500">Tarifaire</span>
            </h2>
            <p className="text-white/50 mt-4">RDV et devis personnalisé au {companyInfo.phone}</p>
          </div>

          {/* Table Header */}
          <div className="hidden md:grid grid-cols-3 gap-4 mb-4 px-6 text-sm text-white/50 uppercase tracking-wider">
            <div>Catégorie</div>
            <div className="text-center">3/4 arrière</div>
            <div className="text-center">Complet</div>
          </div>

          {/* Table Rows */}
          <div className="space-y-3">
            {tintingPrices.map((item) => (
              <div
                key={item.id}
                className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white/5 border border-white/10 rounded-2xl p-6 items-center hover:border-red-500/30 transition-colors"
              >
                <div>
                  <div className="text-white font-semibold text-lg">{item.category}</div>
                  <div className="text-white/40 text-sm">{item.description}</div>
                </div>
                <div className="flex justify-between md:justify-center items-center">
                  <span className="md:hidden text-white/50 text-sm">3/4 arrière</span>
                  <span className="text-white text-xl font-bold">{item.priceRear} €</span>
                </div>
                <div className="flex justify-between md:justify-center items-center">
                  <span className="md:hidden text-white/50 text-sm">Complet</span>
                  <span className="text-red-500 text-xl font-bold">{item.priceComplete} €</span>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <a
              href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
              className="inline-flex items-center gap-3 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-medium transition-colors"
            >
              <Phone size={20} />
              Appeler : {companyInfo.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-12 sm:py-20 bg-neutral-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-red-500 text-sm font-medium uppercase tracking-[0.2em]">
              Portfolio
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
              Nos <span className="text-red-500">Réalisations</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {galleryImages.filter(img => img).map((image, index) => (
              <div key={index} className="aspect-video overflow-hidden rounded-2xl group">
                <img
                  src={image}
                  alt={`Réalisation ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FilmsSolairesPage;
